<template>
  <v-card min-height="90vh" id="tract-detail-card">

    <v-card-title :class="this.mobileCompatible ? 'primary pa-0 flex-nowrap' : 'primary'">
      <TractStatusIcon
      v-if="tract"
      :status="tract.status"
      isHeader
      style="line-height: 0;"/>
      <Icon v-if="tract?.paused" icon="mdi-pause-circle-outline" iconColor="tertiary" :small="false"
            :tooltipText="$t('tractPaused')" />
      <DialogWithTabsTitle
        @menu-clicked="navDrawer = !navDrawer"
        :mobileCompatible="mobileCompatible"
        :titleText="titleText"
        hideRefresh
        @close="removeTractFilter()"
        :tabs="tabs"
        :tab="currentTab"
        @tab-clicked="onTabClicked"
        :enlargeTabs="enlargeNavigationTabs"
        v-if="!loading"
      >
        <template #prepend-base-actions>
          <v-icon v-if="includeRefresh" @click="onRefresh" medium class="mr-2" color="tertiary">
            mdi-refresh
          </v-icon>
            <v-menu v-if="!loading" bottom left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon medium class="mr-2" v-bind="attrs" color="tertiary" data-testid="tract-detail-actions" v-on="on">
                  mdi-dots-vertical
                </v-icon>
              </template>
              <v-list :style="actionMenuStyle" id="tract-detail-actions">
                <v-list-item v-for="(item, i) in headerActions" :key="i" @click="item.action" :disabled="item.disabled" :id="item.id">
                  <v-avatar left>
                    <v-icon color="black">{{ item.icon }}</v-icon>
                  </v-avatar>
                  <v-list-item-title class="subtitle-1">{{ item.tooltipText }}</v-list-item-title>
                </v-list-item>
                <div v-if="mobileCompatible && includeExtraActions">
                  <v-divider style="margin:5% 10% 5% 10%;"></v-divider>
                  <v-list-item v-for="(item, i) in currentExtraActions" :key="i" @click="item.action">
                    <v-avatar left>
                      <v-icon color="black">{{ item.icon }}</v-icon>
                    </v-avatar>
                    <v-list-item-title class="subtitle-1">{{ item.tooltipText }}</v-list-item-title>
                  </v-list-item>
                </div>
              </v-list>
            </v-menu>
        </template>
      </DialogWithTabsTitle>

    </v-card-title>

    <v-card-text :class="mobileCompatible ? 'px-0' : 'mt-3'">
      <v-container fluid fill-height v-if="loading">
        <v-row align="center" justify="center">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </v-container>

      <v-tabs v-model="currentTab" v-if="!loading && !mobileCompatible" id="tract-detail-tabs">
        <v-tab
        v-for="(item, index) in tabs"
        :key="index"
        :value="index"
        :id="kebabCase(item)">
          {{ item }}
        </v-tab>
      </v-tabs>

      <v-window v-model="currentTab" v-if="!loading" touchless>
        <v-window-item :class="mobileCompatible ? '' : 'mt-6'">
          <v-container fluid>
            <v-row>
              <v-col cols="12" :lg="emptyNotes ? 10 : 9" :xl="emptyNotes ? 10 : 9">
                <TractInformation :tract="tract" :landowners="landowners"/>
              </v-col>
              <v-col cols="12" :lg="emptyNotes ? 2 : 3" :xl="emptyNotes ? 2 : 3">
                <TractNotes ref="tractNotes" :tractId="tractId" @emptyNotes="setEmptyNotes" />
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>
        <v-window-item class="mt-3">
          <v-container fluid>
            <v-row dense>
              <v-col cols="12" :class="mobileCompatible ? 'px-0 mt-n3' : 'mt-3'">
                <TractHarvest
                  :tract="tract"
                  :asOfDate="asOfDate"
                  :shouldRefresh="shouldRefreshHarvest"
                  :menuVisible="!mobileCompatible"
                  :class="mobileCompatible ? 'px-0' : ''"
                  :mobileCompatible="mobileCompatible"
                  :distanceFromTract="distanceFromTract"
                  @extra-menu-options="appendExtraMenuOptions"
                  />
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>
        <v-window-item>
          <TractMetrics
            :tract="tract"
            :mobileCompatible="mobileCompatible"
            @extra-menu-options="appendExtraMenuOptions"
          />
        </v-window-item>
        <v-window-item :class="mobileCompatible ? 'ma-1' : 'mt-2 pr-1'">
          <v-row>
            <v-col cols="12" lg="4" xl="3" class="mt-3 pt-0" order-lg="last">
              <TractLocation :tract="tract"/>
            </v-col>
            <v-col cols="12" lg="8" xl="9">
              <v-card flat id="tract-location-map">
                <InteractiveMap
                v-if="reRender"
                :tracts="[tract]"
                :height="mapHeight"
                containerId="tract-detail-card"
                isLocationMap
                showAccountLayer
                includeTractDetailLink
                :showUserLocation="mobileCompatible"
                @tract-detail="$emit('view-tract', $event)"/>
              </v-card>
            </v-col>
          </v-row>
        </v-window-item>
        <v-window-item>
          <TractPayments :propTract="tract" @trigger-refresh="triggerRefresh" @payable-deleted="payableDeleted" @balance-changed="onBalanceChanged" />
        </v-window-item>
        <v-window-item>
          <TractContracts :tractId="tractId" />
        </v-window-item>
        <v-window-item>
          <TractTickets :tractId="tractId" />
        </v-window-item>
        <v-window-item>
          <TractFiles :tract="tract" />
        </v-window-item>
        <v-window-item v-if="isDeliveredTract">
          <TractBids :tractId="tractId" :tract="tract" @outcome-saved="onBidOutcomeSaved" />
        </v-window-item>
        <v-window-item>
          <TractVisits
            ref="tractVisits"
            :tract="tract"
            :mobileCompatible="mobileCompatible"
            @extra-menu-options="appendExtraMenuOptions" />
        </v-window-item>
      </v-window>

      <v-dialog v-model="showingTractPtFormDownload" v-if="showingTractPtFormDownload" width="500">
        <TractPtFormDownload v-if="isGeorgia" :tract="tract" @close-form="toggleTractPtFormDownload()" />
      </v-dialog>

      <v-dialog v-model="customDateRangeDialog" width="400px">
        <CustomTicketDateRangeForm
        v-if="customDateRangeDialog"
        :sinceTime="sinceTime"
        :untilTime="untilTime"
        @dates-chosen="customTimePeriodChosen"
        @close="customDateRangeDialog = false" />
      </v-dialog>

    </v-card-text>

    <Dialog :stateId="dialogId" @dialog-closing="resetDialogs">
      <ModificationHistory entityType="tract" :entityId="tractId" :entityName="$t('tract')" />
    </Dialog>

    <Dialog :stateId="quickCreateDialogId" maxWidth="52em" @dialog-closing="closeQuickCreate" @close="closeQuickCreate">
      <TractNoteForm v-if="noteForm" :tractId="tractId" @tract-note-modified="onNoteModified" @close="closeQuickCreate" />
      <TractVisitForm v-else-if="visitForm" :tract="tract" @visit-mutated="onVisitMutated" />
    </Dialog>

  </v-card>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations.js'
import { TractTypeCategory, UserClaims, TractPayableSpecialization } from '@/utils/Enumerations.js'
import { getTicketPDF } from '@/utils/pdf-formatters/Ticket.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { MOBILE_BREAKPOINT } from '@/utils/DeviceUtility'
import { kebabCase } from 'lodash'
import { migrateLegacyShapefile } from '@/utils/map/ShapefileMigrationModule' // TODO MK: Remove when no longer necessary
import { utcToLocalDate } from '@/utils/DateFormatter'

const LARGE_TABS_HEIGHT_BREAKPOINT = 600
const LOCATION_TAB = 3

export default {
  name: 'TractDetail',
  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    InteractiveMap: () => import('@/components/maps/InteractiveMap.vue'),
    TractInformation: () => import('@/components/tract/tract-detail/TractInformation.vue'),
    CustomTicketDateRangeForm: () => import('@/components/tract/tract-detail/CustomTicketDateRangeForm.vue'),
    TractHarvest: () => import('./TractHarvest.vue'),
    TractMetrics: () => import('./TractMetrics.vue'),
    TractPtFormDownload: () => import('./TractPtFormDownload.vue'),
    TractPayments: () => import('@/components/tract/tract-payables/TractPayables.vue'),
    TractNotes: () => import('@/components/tract/tract-detail/TractNotes.vue'),
    TractContracts: () => import('@/components/tract/tract-detail/TractContracts.vue'),
    TractTickets: () => import('@/components/tract/tract-detail/TractTickets.vue'),
    ModificationHistory: () => import('@/components/admin/ModificationHistory.vue'),
    TractStatusIcon: () => import('@/components/tract/TractStatusIcon.vue'),
    TractFiles: () => import('@/components/tract/tract-form/TractFiles.vue'),
    DialogWithTabsTitle: () => import('@/components/core/DialogWithTabsTitle.vue'),
    TractLocation: () => import('@/components/tract/tract-detail/TractLocation.vue'),
    TractBids: () => import('@/components/tract/tract-detail/TractBids.vue'),
    TractVisits: () => import('@/components/tract/tract-visits/TractVisits.vue'),
    TractNoteForm: () => import('@/components/tract/tract-detail/TractNoteForm.vue'),
    TractVisitForm: () => import('@/components/tract/tract-visits/TractVisitForm.vue')
  },

  props: {
    tractId: {
      type: Number,
      required: true
    },
    tabToOpen: {
      type: Number,
      default: 0
    },
    asOfDate: {
      type: String,
      default: null
    },
    distanceFromTract: {
      type: String,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    units: ['imperial', 'metric'],
    customDateRangeDialog: false,
    reRender: false,
    currentTab: 0,
    tract: undefined,
    loading: true,
    showingTractPtFormDownload: false,
    sinceTime: '',
    untilTime: '',
    emptyNotes: true,
    shouldRefreshHarvest: false,
    dialogId: uniqueDialogId('tract-audit-entries'),
    quickCreateDialogId: uniqueDialogId('tract-quick-create'),
    noteForm: false,
    visitForm: false,
    navDrawer: false,
    extraActions: [],
    landowners: []
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('tract-contents', ['allTractContents']),
    ...mapGetters('ticket', ['filters']),
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('tract-visit-type', ['allTractVisitTypes']),

    isGeorgia () {
      if (!this.tract) return false
      const tractState = this.tract.spot.countrySubdivision || ''
      return tractState === 'GA'
    },

    titleText () {
      if (!this.tract) return ''
      return this.tract.name
    },

    subdivision () {
      if (!this.tract) { return '' }
      if (this.tract.spot.countrySecondarySubdivision !== '' && this.tract.spot.countrySubdivision !== '') {
        return `${this.tract.spot.countrySecondarySubdivision}, ${this.tract.spot.countrySubdivision}`
      }
      return ''
    },

    headerActions () {
      const actions = [
        {
          icon: 'mdi-printer',
          tooltipText: this.$t('printTickets'),
          action: this.openCustomDateRange,
          id: 'print-tickets'
        },
        {
          icon: 'mdi-pencil',
          tooltipText: this.$t('editTractDetail'),
          action: this.editTract,
          disabled: !userAssignedClaim(UserClaims.ContractManager),
          id: 'edit-tract'
        },
        {
          icon: 'mdi-history',
          tooltipText: this.$t('viewModifications'),
          action: this.viewModificationHistory,
          id: 'view-modifications'
        }
      ]

      if (this.isGeorgia) {
        actions.unshift({
          icon: 'mdi-file-pdf-box',
          tooltipText: this.$t('downloadPTForm'),
          action: this.toggleTractPtFormDownload,
          large: true,
          id: 'download-pt-form'
        })
      }

      if (this.mobileCompatible) {
        actions.unshift({
          icon: 'mdi-book-outline',
          tooltipText: this.$t('createNote'),
          action: this.openNoteForm,
          id: 'tract-detail-create-note'
        }, {
          icon: 'mdi-compass',
          tooltipText: this.$t('logVisit'),
          action: this.openVisitForm,
          id: 'tract-detail-log-visit'
        })
      }
      return actions
    },

    loggerHeaders () {
      return [
        {
          text: 'Name',
          value: 'name'
        },
        {
          text: 'Code',
          value: 'code'
        }
      ]
    },

    tabs () {
      return [
        this.$t('properties'),
        this.$t('tractCostAnalysis'),
        this.$t('metrics'),
        this.$t('location'),
        this.$t('payments'),
        this.$t('contracts'),
        this.$t('tickets'),
        this.$t('files'),
        ...(this.isDeliveredTract ? [this.$t('bids')] : []),
        this.$t('visits')
      ]
    },

    isDeliveredTract () {
      return this.tract?.type?.category === TractTypeCategory.Delivered.value
    },

    actionMenuStyle () {
      const h = this.windowHeight
      const maxHeight = h - 25
      if (h > 800) {
        return {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: `${maxHeight}px`
        }
      } else {
        return {}
      }
    },

    includeExtraActions () {
      return !!this.extraActions.find(a => a.tab === this.tabs[this.currentTab])
    },

    currentExtraActions () {
      if (!this.includeExtraActions) return undefined
      return this.extraActions.find(a => a.tab === this.tabs[this.currentTab])?.actions.filter(a => !!a.icon)
    },

    includeRefresh () {
      return this.mobileCompatible && !!this.extraActions.find(a => a.tab === this.tabs[this.currentTab])?.actions.find(a => a.id === 'refresh')
    },

    mobileCompatible () {
      return this.$vuetify.breakpoint.width < MOBILE_BREAKPOINT
    },

    enlargeNavigationTabs () {
      return this.$vuetify.breakpoint.height > LARGE_TABS_HEIGHT_BREAKPOINT
    },

    mapHeight () {
      const heightOffset = 160
      return `max(calc(${this.topDialogState.fullscreen ? 100 : 90}vh - ${heightOffset}px), 300px)`
    }
  },

  watch: {
    tractId () {
      this.loadTract()
    }
  },

  async mounted () {
    this.currentTab = this.mobileCompatible && this.tabToOpen === 0 ? LOCATION_TAB : this.tabToOpen
    this.reRender = !this.reRender
    await this.loadTract()
  },

  methods: {
    ...mapMutations('global', ['setRouterJump']),
    ...mapMutations('ticket', ['applyFilter']),
    ...mapActions('tract', ['fetchTract', 'fetchAllTractTickets', 'getWeatherForTract', 'fetchTractLandowners']),
    ...mapMutations('ticket', ['removeFilter']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract-visit-type', ['fetchTractVisitTypes']),

    formatMoney,
    numberWithCommas,
    kebabCase,

    categoryFromInt: (x) => TractTypeCategory.fromInt(x),

    toggleTractPtFormDownload () {
      this.showingTractPtFormDownload = !this.showingTractPtFormDownload
    },

    editTract () {
      this.$emit('edit-tract', this.tract)
    },

    openCustomDateRange () {
      this.customDateRangeDialog = true
    },

    async printTractTickets () {
      const tractTickets = await this.fetchAllTractTickets({
        tractId: this.tract.tractId,
        sinceTime: this.sinceTime,
        untilTime: this.untilTime
      })
      if (tractTickets.length > 0) {
        getTicketPDF(tractTickets,
          this.companyInfo,
          [this.tract],
          `${this.tract.name}_Tickets_${this.stringForFileName(this.sinceTime)}_${this.stringForFileName(this.untilTime)}`
        )
      } else {
        this.setSnackError('No available tickets for selection.')
      }
    },

    stringForFileName (date) {
      return utcToLocalDate(date, 'MM-DD-YYYY')
    },

    customTimePeriodChosen (dateRange) {
      this.customDateRangeDialog = false
      this.sinceTime = dateRange.sinceTime
      this.untilTime = dateRange.untilTime
      this.printTractTickets()
    },

    setEmptyNotes (val) {
      this.emptyNotes = val
    },

    removeTractFilter () {
      if (this.filters?.filter) {
        var filter = this.filters?.filter(f => f.name === 'tract')[0]
        this.removeFilter(filter)
      }
    },

    viewModificationHistory () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '75vw' })
      this.auditChanges = true
    },

    resetDialogs () {
      this.auditChanges = false
    },

    openNoteForm () {
      this.noteForm = true
      this.openOrUpdateDialog(this.quickCreateDialogId)
    },

    async openVisitForm () {
      if (!this.allTractVisitTypes.length) await this.fetchTractVisitTypes()
      if (!this.allTractVisitTypes.length) {
        this.setSnackError(this.$t('noTractVisitTypesDefined'))
        return
      }
      this.visitForm = true
      this.openOrUpdateDialog(this.quickCreateDialogId)
    },

    closeQuickCreate () {
      this.closeDialogsAtOrAbove(this.quickCreateDialogId)
      this.noteForm = false
      this.visitForm = false
    },

    onNoteModified () {
      if (this.$refs.tractNotes) this.$refs.tractNotes.refreshTractNotes()
    },

    onVisitMutated () {
      this.closeQuickCreate()
      if (this.$refs.tractVisits) this.$refs.tractVisits.refresh()
    },

    triggerRefresh () {
      this.shouldRefreshHarvest = true
      this.$nextTick(() => {
        this.shouldRefreshHarvest = false
      })
    },

    navItemClicked (e) {
      this.currentTab = e
    },

    appendExtraMenuOptions (extraMenuOptions) {
      if (!this.extraActions.find(ea => ea.tab === extraMenuOptions.tab)) {
        this.extraActions.push(extraMenuOptions)
      } else {
        this.extraActions.find(ea => ea.tab === extraMenuOptions.tab).actions = extraMenuOptions.actions
      }
    },

    onTabClicked (t) {
      this.currentTab = t
    },

    onBalanceChanged () {
      this.fetchTractData()
      this.$emit('changed')
    },

    payableDeleted () {
      this.onBalanceChanged()
      this.triggerRefresh()
    },

    async onBidOutcomeSaved () {
      await this.fetchTractData()
      this.triggerRefresh()
    },

    async loadTract () {
      this.loading = true
      try {
        await this.fetchTractData()
      } finally {
        this.loading = false
      }
    },

    async fetchTractData () {
      const tractResponse = await this.fetchTract(this.tractId)
      this.tract = tractResponse
      this.tract.bondBalance = tractResponse.tractPayables.filter(p => p.specialization === TractPayableSpecialization.PerformanceBond.value).reduce((acc, pb) => acc + pb.amount, 0)
      this.landowners = await this.fetchTractLandowners(this.tractId)

      await migrateLegacyShapefile(this.tract)
    },

    onRefresh () {
      if (this.includeRefresh) {
        const action = this.extraActions.find(a => a.tab === this.tabs[this.currentTab])?.actions?.find(a => a.id === 'refresh')?.action
        action()
      }
    }
  }
}

</script>
